import React from 'react';
import { StyleProp, Text, View, ViewStyle } from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { Palette, RADIUS } from '../../assets/theme';
import { makeStyles, useAppTheme } from '../../theme/ThemeContext';

export type BadgeTone = 'neutral' | 'primary' | 'brand' | 'success' | 'warning' | 'danger' | 'info';

export function toneColors(colors: Palette, tone: BadgeTone): { fg: string; bg: string } {
  switch (tone) {
    case 'primary': return { fg: colors.primary, bg: colors.primarySoft };
    case 'brand': return { fg: colors.brand, bg: colors.brandSoft };
    case 'success': return { fg: colors.success, bg: colors.successSoft };
    case 'warning': return { fg: colors.warning, bg: colors.warningSoft };
    case 'danger': return { fg: colors.danger, bg: colors.dangerSoft };
    case 'info': return { fg: colors.info, bg: colors.infoSoft };
    default: return { fg: colors.textSecondary, bg: colors.surfaceAlt };
  }
}

type BadgeProps = {
  label: string;
  tone?: BadgeTone;
  icon?: React.ComponentProps<typeof MaterialCommunityIcons>['name'];
  /** Show a small colored dot before the label instead of an icon. */
  dot?: boolean;
  style?: StyleProp<ViewStyle>;
};

/** Compact status pill (soft background + strong foreground). */
export default function Badge({ label, tone = 'neutral', icon, dot, style }: BadgeProps) {
  const styles = useStyles();
  const { colors } = useAppTheme();
  const { fg, bg } = toneColors(colors, tone);

  return (
    <View style={[styles.badge, { backgroundColor: bg }, style]}>
      {dot ? <View style={[styles.dot, { backgroundColor: fg }]} /> : null}
      {icon ? <MaterialCommunityIcons name={icon} size={13} color={fg} /> : null}
      <Text style={[styles.label, { color: fg }]} numberOfLines={1}>{label}</Text>
    </View>
  );
}

const useStyles = makeStyles(() => ({
  badge: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 5,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: RADIUS.pill,
  },
  dot: { width: 6, height: 6, borderRadius: 3 },
  label: { fontSize: 12, fontWeight: '700' },
}));
